import '@lilara/ui-web'

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router } from '@angular/router'
import type { Sidebar, SidebarUser } from '@lilara/ui-web'
import { Store } from '@ngrx/store'
import { filter } from 'rxjs/operators'
import { setNavbarCollapse } from 'src/app/actions/user-settings.actions'
import { AnalyticsEvents } from 'src/app/constants/analytics'
import { HubtypeUser } from 'src/app/models/hubtype-user'
import * as fromRoot from 'src/app/reducers'
import { CaseArchiveGuard } from 'src/app/services/case-archive.guard'
import { UserService } from 'src/app/services/hubtype-api/user.service'
import { PermissionsService } from 'src/app/services/permissions.service'
import { Analytics } from 'src/app/utils/analytics'
import { AwayDialogComponent } from './away-dialog.component'
import {
  buildSidebarNavData,
  type SidebarPermissions,
} from './sidebar-nav-data'
import {
  COLLAPSE_BREAKPOINT_PX,
  isAwayModalDismissed,
  setAwayModalDismissed,
} from './sidebar-state'
import {
  buildSidebarUser,
  buildUserMenuItems,
  mapUserStatus,
  USER_MENU_ITEM_IDS,
  type SidebarUserStatus,
  type UserMenuItem,
} from './sidebar-user-data'

@Component({
  selector: 'app-lilara-sidebar-wrapper',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [AwayDialogComponent],
  host: {
    '[class.sidebar-collapsed]': 'isCollapsed()',
  },
  template: `
    <div class="lilara-sidebar-wrapper" data-ht-app-nav>
      <lilara-sidebar #sidebar [attr.collapsed]="isCollapsed() ? '' : null" />
    </div>
    @if (showAwayDialog()) {
      <app-away-dialog (close)="onAwayDialogClose($event)" />
    }
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-shrink: 0;
        height: 100vh;
      }
      .lilara-sidebar-wrapper {
        flex-shrink: 0;
        height: 100vh;
        padding: var(--lilara-app-padding, var(--lilara-spacing-s, 12px));
        position: relative;
        z-index: 1;
      }
      /* No width animation until the sidebar has synced its collapsed
         state once — whatever paints first must not transition. */
      lilara-sidebar:not(.ht-booted) {
        transition: none !important;
      }
    `,
  ],
})
export class LilaraSidebarWrapperComponent implements AfterViewInit {
  readonly #store = inject(Store<fromRoot.State>)
  readonly #router = inject(Router)
  readonly #permissionsService = inject(PermissionsService)
  readonly #userService = inject(UserService)
  readonly #caseArchiveGuard = inject(CaseArchiveGuard)
  readonly #destroyRef = inject(DestroyRef)

  readonly sidebarRef = viewChild<ElementRef<Sidebar>>('sidebar')

  readonly isCollapsed = toSignal(
    this.#store.select(fromRoot.isNavBarCollapsed),
    // The initial value was read from localStorage in main.ts (before
    // bootstrap) and stored on window.__htNavbarCollapsed. Using it here
    // ensures the [attr.collapsed] binding paints with the right state on
    // the very first render — no expanded -> collapsed flash on boot.
    { initialValue: (window as any).__htNavbarCollapsed ?? true }
  )
  private readonly me = toSignal(this.#store.select(fromRoot.getMeState), {
    initialValue: null as HubtypeUser | null,
  })
  private readonly hasCaseArchiveAccess = toSignal(
    this.#caseArchiveGuard.canActivate(),
    { initialValue: false }
  )

  readonly showAwayDialog = signal(false)
  private awayDialogShown = false
  private collapsedChangeInitialized = false
  private readonly userStatus = linkedSignal<SidebarUserStatus>(() => {
    const me = this.me()
    return me ? mapUserStatus(me) : 'online'
  })

  private readonly permissions = computed<SidebarPermissions>(() => {
    const me = this.me()
    if (!me) {
      return {
        isAdmin: false,
        isManager: false,
        isAgent: false,
        hasAiKnowledgeAccess: false,
        hasQaEvaluatorAccess: false,
        hasWhatsappCallLogsAccess: false,
        hasCaseArchiveAccess: false,
        hasContactsAccess: false,
        showContactsListLink: false,
        showCampaignsV2Nav: false,
        hasCampaignsAccess: false,
        hasAnalyticsAccess: false,
        hasAnalyticsConversationsAccess: false,
        hasCopilotAccess: false,
        hasOrgSettingsAccess: false,
      }
    }
    const ps = this.#permissionsService
    return {
      isAdmin: me.is_admin,
      isManager: me.is_manager,
      isAgent: me.is_agent,
      hasAiKnowledgeAccess: me.is_admin,
      hasQaEvaluatorAccess: ps.hasQaEvaluatorAccess() === true,
      hasWhatsappCallLogsAccess: ps.hasWhatsappCallLogsAccess() === true,
      hasCaseArchiveAccess: this.hasCaseArchiveAccess(),
      hasContactsAccess: ps.hasContactsAccess() === true,
      showContactsListLink: ps.showContactsListLink() === true,
      showCampaignsV2Nav: ps.showCampaignsV2Nav(),
      hasCampaignsAccess: ps.hasCampaignsAccess() === true,
      hasAnalyticsAccess: ps.hasAnalyticsAccess() === true,
      hasAnalyticsConversationsAccess:
        ps.hasAnalyticsConversationsAccess() === true,
      hasCopilotAccess: ps.hasCopilotAccess() === true,
      hasOrgSettingsAccess: ps.hasOrgSettingsAccess() === true,
    }
  })

  private readonly navData = computed(() =>
    buildSidebarNavData(this.permissions())
  )

  private readonly sidebarUser = computed<SidebarUser | undefined>(() => {
    const me = this.me()
    if (!me) return undefined
    return buildSidebarUser(me, this.userStatus())
  })

  private readonly userMenuItems = computed<UserMenuItem[]>(() =>
    buildUserMenuItems(this.userStatus())
  )

  constructor() {
    effect(() => {
      const me = this.me()
      if (me && me.isAway && !this.awayDialogShown) {
        this.awayDialogShown = true
        if (!isAwayModalDismissed()) {
          this.showAwayDialog.set(true)
        }
      }
    })

    effect(() => {
      this.syncSidebarProps()
    })

    this.#router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.syncActivePath()
      })
  }

  ngAfterViewInit() {
    const el = this.sidebarRef()?.nativeElement
    if (!el) return

    // Apply the persisted collapse state BEFORE Lit's first update (which
    // runs as a microtask after this synchronous render block) so the first
    // paint already matches — no expanded -> collapsed flash on boot.
    const stored = (window as any).__htNavbarCollapsed
    el.collapsed =
      stored !== null && stored !== undefined ? stored : this.isCollapsed()

    // Enable transitions only AFTER Lit's first render completes. The
    // sidebar's shadow DOM has `transition: width 0.2s ease` on :host;
    // if `ht-booted` is added before Lit's first update, the width change
    // from default → collapsed animates on boot.
    ;(el.updateComplete as Promise<unknown> | undefined)?.then(() => {
      el.classList.add('ht-booted')
    })

    this.syncActivePath()

    el.addEventListener('navigate', (e: Event) => {
      this.onNavigate(e)
    })
    el.addEventListener('collapsed-change', (e: Event) => {
      this.onCollapsedChange(e)
    })
    el.addEventListener('user-menu-select', (e: Event) => {
      this.onUserMenuSelect(e)
    })
  }

  onAwayDialogClose(keepWorking: boolean) {
    this.showAwayDialog.set(false)
    if (!keepWorking) {
      setAwayModalDismissed(false)
      this.#userService.setAvailable().subscribe(() => {
        this.userStatus.set('online')
      })
    }
  }

  private syncSidebarProps() {
    const el = this.sidebarRef()?.nativeElement
    if (!el) return

    const navData = this.navData()
    const user = this.sidebarUser()
    const menuItems = this.userMenuItems()

    el.sections = navData.sections
    el.footerSections = navData.footerSections
    el.user = user
    ;(el as any).userMenuItems = menuItems
    ;(el as any).collapseBreakpoint = COLLAPSE_BREAKPOINT_PX
    el.label = 'Main navigation'
  }

  private syncActivePath() {
    const el = this.sidebarRef()?.nativeElement
    if (!el) return
    el.activePath = this.#router.url
  }

  private onNavigate(e: Event) {
    const detail = (e as CustomEvent<{ id: string; href?: string }>).detail
    if (!detail.href) return

    // External links (Help Center) navigate natively via the anchor's
    // target="_blank" — never intercepted, so the <a> handles them.
    if (detail.href.startsWith('http')) return

    const navReloadMap = this.navData().navReloadMap
    const shouldReload = navReloadMap.get(detail.id) ?? true
    if (shouldReload) {
      // React app routes: full-page reload with cache-buster
      e.preventDefault()
      window.location.href = `${detail.href}?v=${Date.now()}`
      return
    }

    e.preventDefault()
    this.#router.navigate([detail.href])
  }

  private onCollapsedChange(e: Event) {
    // The sidebar emits a synthetic initial collapsed-change right after
    // collapseBreakpoint is set (its "emit initial state" for the media
    // query). That dispatch would overwrite the collapse state restored
    // from localStorage — ignore the first emission.
    if (!this.collapsedChangeInitialized) {
      this.collapsedChangeInitialized = true
      e.preventDefault()
      return
    }
    const next = (e as CustomEvent<boolean>).detail
    Analytics.event(AnalyticsEvents.NAVBAR_COLLAPSE_UNCOLLAPSE, {
      collapse: next,
    })
    this.#store.dispatch(setNavbarCollapse({ value: next }))
  }

  private onUserMenuSelect(e: Event) {
    const detail = (e as CustomEvent<{ id: string }>).detail
    if (!detail) return

    if (detail.id === USER_MENU_ITEM_IDS.status) {
      this.toggleAwayStatus(e)
    } else if (detail.id === USER_MENU_ITEM_IDS.logOut) {
      e.preventDefault()
      this.logout()
    }
  }

  private toggleAwayStatus(e: Event) {
    const me = this.me()
    if (!me) return

    const currentStatus = this.userStatus()
    if (currentStatus === 'online') {
      e.preventDefault()
      this.userStatus.set('away')
      this.#userService.setAway().subscribe({
        error: () => this.userStatus.set('online'),
      })
      this.showAwayDialog.set(true)
      setAwayModalDismissed(true)
    } else {
      this.userStatus.set('online')
      setAwayModalDismissed(false)
      this.#userService.setAvailable().subscribe({
        error: () => this.userStatus.set('away'),
      })
    }
  }

  private logout() {
    this.#userService
      .setOffline()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        window.location.replace(`${window.location.origin}/sign-in`)
      })
  }
}
